/**
 * @file: MemberChatButtonByName.tsx
 *
 * "Start chat" button for a member known only by NAME — a delivery / sampling
 * check author is persisted as a name string, with no member id. Searches
 * contacts by name and opens the chat directly on a single match, or shows an
 * action sheet (name · role) to pick when several members share the name.
 * Hidden for the current user.
 *
 * Members that DO carry an org member id should use {@link MemberChatButton}.
 */

import { ChatLaunchIcon } from '@euler/app/flows/chat/components/ChatLaunchIcon';
import { useFloatingChat } from '@euler/app/flows/chat/lib/floating/context';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useAlert } from '@euler/functions/ux/useAlert';
import { ChatContact } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FC, memo, useCallback } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const MemberChatButtonByName: FC<{
  name: string | undefined;
  size?: number;
  style?: StyleProp<ViewStyle>;
}> = memo(({ name, size = 16, style }) => {
  const { me } = useChatSession();
  const { chatService } = useServiceFactory();
  const { openChat } = useFloatingChat();
  const loading = useAppLoading();
  const alert = useAlert();
  const { showActionSheetWithOptions } = useActionSheet();

  const onPress = useCallback(async () => {
    if (!name) return;
    const matches: ChatContact[] | undefined = await loading.showWithAction(
      () => chatService.listContacts({ name }),
    );
    if (!matches) return; // request failed — already alerted by the loader
    if (matches.length === 0) {
      void alert({ title: '未找到联系人', message: `找不到“${name}”` });
      return;
    }
    if (matches.length === 1) {
      openChat({ kind: 'member', orgMemberId: matches[0].memberId });
      return;
    }
    // Several members share the name — let the user pick.
    const labels = matches.map(c =>
      c.jobTitle ? `${c.name}（${c.jobTitle}）` : c.name,
    );
    showActionSheetWithOptions(
      {
        title: '选择联系人',
        options: [...labels, '取消'],
        cancelButtonIndex: labels.length,
      },
      i => {
        if (i != null && i < matches.length) {
          openChat({ kind: 'member', orgMemberId: matches[i].memberId });
        }
      },
    );
  }, [name, loading, chatService, alert, openChat, showActionSheetWithOptions]);

  // Hidden for the current user (you can't chat yourself). Comparing by name is
  // a heuristic — these authors have no member id to compare against.
  if (!name || name === me.user.name) return null;
  return (
    <TouchableOpacity onPress={onPress} hitSlop={kHitSlop} style={style}>
      <ChatLaunchIcon size={size} />
    </TouchableOpacity>
  );
});
